/**
 * PerformerClaimPage — a performer lands here from the promo invite email.
 * Shows the event + commission offer; they log in or create an account,
 * accept, and get their tracking link + freshly claimed public profile page.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Loader2, Mic2, DollarSign, CheckCircle2, Copy, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PerformerClaimPage() {
  const { token } = useParams();
  const { user, token: authToken, login, register } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('register');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/performer-invites/${token}`)
      .then(res => {
        setData(res.data);
        setForm(f => ({ ...f, name: res.data.invite.performer_name || '' }));
      })
      .catch(err => setError(err.response?.data?.detail || 'Invite not found'));
  }, [token]);

  const accept = async (tok) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/performer-invites/${token}/accept`, {},
        { headers: { Authorization: `Bearer ${tok || authToken}` } });
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not accept the invite');
    } finally { setBusy(false); }
  };

  const authThenAccept = async () => {
    setBusy(true);
    try {
      const r = mode === 'register'
        ? await register(form.email, form.password, form.name)
        : await login(form.email, form.password);
      if (r?.success === false) { toast.error(r.error || 'Sign in failed'); setBusy(false); return; }
      await accept(localStorage.getItem('eventism_token'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sign in failed');
      setBusy(false);
    }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center p-6"><p className="text-muted-foreground" data-testid="claim-error">{error}</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-400" /></div>;

  const { invite, event } = data;

  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="performer-claim-page">
      <div className="max-w-xl mx-auto space-y-5">
        <div className="text-center space-y-1">
          <Badge className="bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30 gap-1.5"><Mic2 className="h-3 w-3" /> Performer invite</Badge>
          <h1 className="text-2xl font-bold" data-testid="claim-heading">{invite.performer_name}, get paid to promote</h1>
          <p className="text-sm text-muted-foreground">You're invited to promote {event.name} as a partner.</p>
        </div>

        <Card className="overflow-hidden border-border">
          {event.image_url && <img src={event.image_url} alt={event.name} className="w-full h-36 object-cover" />}
          <CardContent className="p-4 space-y-2">
            <h2 className="font-bold" data-testid="claim-event-name">{event.name}</h2>
            <p className="text-xs text-muted-foreground">{event.event_date}{event.venue_name ? ` · ${event.venue_name}` : ''}</p>
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-300 flex items-center gap-2" data-testid="claim-commission">
              <DollarSign className="h-4 w-4 flex-shrink-0" /> You earn <strong>{invite.commission_label}</strong> through your link
            </div>
            <p className="text-[11px] text-muted-foreground">Accepting also claims your public performer page — bio, gallery, Instagram, and every event you play.</p>
          </CardContent>
        </Card>

        {result ? (
          <Card className="border-emerald-500/40">
            <CardContent className="p-6 space-y-3 text-center" data-testid="claim-success">
              <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto" />
              <h2 className="font-bold text-lg">You're in!</h2>
              <div className="text-left space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Your promo link</p>
                <div className="flex gap-2">
                  <Input readOnly value={result.landing_url} className="text-xs" data-testid="claim-landing-url" />
                  <Button size="icon" variant="outline" onClick={() => { navigator.clipboard.writeText(result.landing_url); toast.success('Link copied'); }} data-testid="claim-copy-link"><Copy className="h-4 w-4" /></Button>
                </div>
                <p className="text-[11px] text-muted-foreground">Every ticket sold through this link earns you {result.commission_label}. Track earnings + cash out in your Street Team wallet.</p>
              </div>
              <div className="flex gap-2 justify-center pt-1">
                <Button asChild className="rounded-full font-semibold" data-testid="claim-view-profile">
                  <Link to={result.profile_url}>View your page <ExternalLink className="h-3.5 w-3.5 ml-1.5" /></Link>
                </Button>
                <Button asChild variant="outline" className="rounded-full">
                  <Link to="/dashboard/street-team">Wallet &amp; payouts</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : invite.status === 'accepted' ? (
          <Card><CardContent className="p-6 text-center text-sm text-muted-foreground" data-testid="claim-already">
            This invite was already claimed.{invite.profile_url && <> View the page <Link className="text-indigo-400 underline" to={invite.profile_url}>here</Link>.</>}
          </CardContent></Card>
        ) : user ? (
          <Button className="w-full rounded-full font-bold" disabled={busy} onClick={() => accept()} data-testid="claim-accept-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Accept & claim my page'}
          </Button>
        ) : (
          <Card className="border-border">
            <CardContent className="p-4 space-y-3" data-testid="claim-auth-form">
              <div className="flex gap-1 text-xs font-semibold">
                <button className={`px-3 py-1.5 rounded-full ${mode === 'register' ? 'bg-indigo-500 text-white' : 'bg-muted text-muted-foreground'}`} onClick={() => setMode('register')} data-testid="claim-mode-register">Create account</button>
                <button className={`px-3 py-1.5 rounded-full ${mode === 'login' ? 'bg-indigo-500 text-white' : 'bg-muted text-muted-foreground'}`} onClick={() => setMode('login')} data-testid="claim-mode-login">I have an account</button>
              </div>
              {mode === 'register' && (
                <Input placeholder="Your name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="claim-name-input" />
              )}
              <Input type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="claim-email-input" />
              <Input type="password" placeholder="Password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="claim-password-input" />
              <Button className="w-full rounded-full font-bold" disabled={busy || !form.email || !form.password} onClick={authThenAccept} data-testid="claim-auth-accept-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Accept & claim my page'}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
